import { Box, Button, FormControl, Row, Select, Text } from 'native-base'
import React, { RefObject, useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { Keyboard, Platform, StyleSheet } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import {
  CheckBoxList,
  ControlledField,
  KeyboardAwareScrollView,
  UpdatePointsRewardInfo,
  WebBackButtonBar,
} from '~components'
import { RFColors, WEB, mobileMinWidth, userDetailsFormWidth, webNavBarHeight } from '~constants'
import {
  useCallback,
  useDeviceInfo,
  useRef,
  useState,
  useTranslation,
  useUpdateUserDetailsForm,
} from '~hooks'
import {
  useGetCountriesList,
  useGetInterestsList,
  useGetStatesList,
  useGetCitiesList,
} from '~query-hooks'

type UserDetailsFields = {
  gender: RefObject<typeof Select>
  location: RefObject<typeof Select>
}

export const UserDetailsFormScreen = (props: UserDetailsFormScreenProps): JSX.Element => {
  const {
    route: { params } = {},
    navigation: { goBack },
  } = props

  const { isWeb, shouldApplyWideScreenStyles, shouldApplyDesktopStyles } = useDeviceInfo()
  const shouldRenderBackButtonBar = isWeb && shouldApplyWideScreenStyles
  const { showRewardInfo } = params || {}
  const { t } = useTranslation()
  const fieldsRef: UserDetailsFields = {
    gender: useRef<typeof Select>(null),
    location: useRef<typeof Select>(null),
  }
  const { countries: locationList } = useGetCountriesList()
  const { interests: interestsList, isLoadingInterests } = useGetInterestsList()
  const [isSelectGenderFocused, setIsSelectGenderFocused] = useState<boolean>(false)
  const [isSelectLocationFocused, setIsSelectedLocationFocused] = useState<boolean>(false)
  const {
    control,
    errors,
    submit,
    isSubmitting,
    setValue,
    getValues,
    hasAnyFieldData,
    clearErrors,
    handleZipChange,
    handleZipBlur,
    isLocationLoading,
  } = useUpdateUserDetailsForm()
  const selectedCountryValue = useWatch({ control, name: 'countryId' }) as string | undefined
  const selectedStateId = useWatch({ control, name: 'stateId' }) as string | undefined
  const normalizedSelectedCountryId = useMemo(() => {
    if (!selectedCountryValue) {
      return ''
    }
    const match = locationList?.find((option) => option.value === selectedCountryValue)
    return match ? match.value : ''
  }, [locationList, selectedCountryValue])

  const { states: statesList } = useGetStatesList(normalizedSelectedCountryId, {
    enabled: Boolean(normalizedSelectedCountryId),
  })
  const { cities: citiesList } = useGetCitiesList(selectedStateId)
  const [localInterests, setLocalInterests] = useState<string[]>([])

  const closeGenderPicker = useCallback(() => {
    setIsSelectGenderFocused(false)
  }, [])
  const closeLocationPicker = useCallback(() => {
    setIsSelectedLocationFocused(false)
  }, [])

  const handleGenderPickerValueChange = useCallback(() => {
    closeGenderPicker()
  }, [closeGenderPicker])

  const onLocationSelectValueChange = useCallback(() => {
    closeLocationPicker()
  }, [closeLocationPicker])

  const handleCountryChange = useCallback(
    (value: string) => {
      setValue('countryId', value, { shouldValidate: false })
      setValue('location', value, { shouldValidate: false })
      setValue('stateId', '', { shouldValidate: false })
      setValue('state', '', { shouldValidate: false })
      setValue('cityId', '', { shouldValidate: false })
      setValue('city', '', { shouldValidate: false })
      onLocationSelectValueChange()
    },
    [onLocationSelectValueChange, setValue]
  )

  const handleStateChange = useCallback(
    (value: string) => {
      const normalizedValue = value?.toString?.() ?? ''
      const selectedState = statesList?.find((item) => item.value === normalizedValue)
      setValue('stateId', normalizedValue, { shouldValidate: false })
      setValue('state', selectedState?.label || '', { shouldValidate: false })
      setValue('cityId', '', { shouldValidate: false })
      setValue('city', '', { shouldValidate: false })
      if (!selectedState) {
        setValue('stateId', '', { shouldValidate: false })
      }
    },
    [setValue, statesList]
  )

  const handleCityChange = useCallback(
    (value: string) => {
      const normalizedValue = value?.toString?.() ?? ''
      const selectedCity = citiesList?.find((item) => item.value === normalizedValue)
      setValue('cityId', normalizedValue, { shouldValidate: false })
      setValue('city', selectedCity?.label || '', { shouldValidate: false })
      if (!selectedCity) {
        setValue('cityId', '', { shouldValidate: false })
      }
    },
    [citiesList, setValue]
  )

  useEffect(() => {
    if (!statesList?.length) {
      return
    }

    const currentStateId = getValues('stateId')
    const currentStateName = getValues('state')
    const matchedState = currentStateId
      ? statesList.find((stateOption) => stateOption.value === currentStateId)
      : statesList.find((stateOption) => stateOption.label === currentStateName)
    if (matchedState && getValues('stateId') !== matchedState.value) {
      setValue('stateId', matchedState.value, { shouldValidate: false })
    }
    if (matchedState && getValues('state') !== matchedState.label) {
      setValue('state', matchedState.label, { shouldValidate: false })
    }
  }, [statesList, getValues, setValue])

  useEffect(() => {
    if (!citiesList?.length) {
      return
    }

    const currentCityId = getValues('cityId')
    const currentCityName = getValues('city')
    const matchedCity = currentCityId
      ? citiesList.find((cityOption) => cityOption.value === currentCityId)
      : citiesList.find((cityOption) => cityOption.label === currentCityName)
    if (matchedCity && getValues('cityId') !== matchedCity.value) {
      setValue('cityId', matchedCity.value, { shouldValidate: false })
    }
    if (matchedCity && getValues('city') !== matchedCity.label) {
      setValue('city', matchedCity.label, { shouldValidate: false })
    }
  }, [citiesList, getValues, setValue])

  // Initialize local interests from form values
  useEffect(() => {
    const formInterests = getValues('interests') || []
    setLocalInterests(formInterests)
    // Clear any existing errors since all fields are optional
    clearErrors()
  }, [getValues, clearErrors])

  const isSubmitDisabled = isSubmitting || !hasAnyFieldData

  const handleSaveButtonPress = useCallback(async () => {
    Keyboard.dismiss()
    await submit()
  }, [submit])

  const handleInterestPress = useCallback(
    (value: string) => {
      const isSelected = localInterests.includes(value)
      const newInterests = isSelected
        ? localInterests.filter((interest) => interest !== value)
        : [...localInterests, value]
      setLocalInterests(newInterests)
      setValue('interests', newInterests)
    },
    [localInterests, setValue]
  )

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
      <KeyboardAwareScrollView
        contentContainerStyle={styles.keyboardAwareScrollView}
        extraScrollHeight={10}
      >
        <Box flex={1} w="full">
          {shouldRenderBackButtonBar ? (
            <WebBackButtonBar />
          ) : (
            <Text fontSize="xl" bold mt="8" {...(showRewardInfo && { mb: '2' })}>
              {t('user_details_form_screen.profile')}
            </Text>
          )}
          {showRewardInfo ? <UpdatePointsRewardInfo /> : null}
          <Box {...(shouldApplyDesktopStyles && { pb: webNavBarHeight, my: 'auto' })}>
            <Box w="full" rounded="lg" bgColor="white" p="4" {...(showRewardInfo && { mt: '4' })}>
              <Text fontSize="lg" bold mb="6">
                {t('user_details_form_screen.edit_profile')}
              </Text>
              <ControlledField.Input
                {...{ control, errors }}
                editable={false}
                label={t('common.email')}
                name="email"
                testID="emailInput"
              />

              <ControlledField.Input
                {...{ control, errors }}
                label={t('common.zip_code')}
                name="zipCode"
                placeholder={
                  isLocationLoading ? t('form.location_fetching') : t('form.user_details.zip_code_placeholder')
                }
                testID="zipCodeInput"
                onChangeText={handleZipChange}
                onBlur={handleZipBlur}
                editable={!isLocationLoading}
              />
              <ControlledField.Select
                {...{ control, errors }}
                isFocused={isSelectLocationFocused}
                label={t('common.country')}
                name="countryId"
                onClose={closeLocationPicker}
                onValueChange={handleCountryChange}
                placeholder={
                  isLocationLoading ? t('form.location_fetching') : t('form.user_details.country_placeholder')
                }
                ref={fieldsRef.location}
                size="xl"
                values={locationList || []}
                isDisabled={isLocationLoading}
              />

              <ControlledField.Select
                {...{ control, errors }}
                label={t('common.state')}
                name="stateId"
                placeholder={
                  isLocationLoading ? t('form.location_fetching') : t('form.user_details.state_placeholder')
                }
                size="xl"
                values={statesList || []}
                isDisabled={isLocationLoading || !statesList?.length}
                onValueChange={handleStateChange}
              />

              <ControlledField.Select
                {...{ control, errors }}
                label={t('common.city')}
                name="cityId"
                placeholder={
                  isLocationLoading ? t('form.location_fetching') : t('form.user_details.city_placeholder')
                }
                size="xl"
                values={citiesList || []}
                isDisabled={isLocationLoading || !citiesList?.length}
                onValueChange={handleCityChange}
              />

              <ControlledField.Input
                {...{ control, errors }}
                keyboardType="numeric"
                label={t('common.birth_date')}
                maxLength={4}
                name="birthDate"
                testID="birthDateInput"
              />

              <ControlledField.Select
                {...{ control, errors }}
                onClose={closeGenderPicker}
                isFocused={isSelectGenderFocused}
                label={t('common.gender')}
                name="gender"
                onValueChange={handleGenderPickerValueChange}
                placeholder={t('form.user_details.gender_placeholder')}
                ref={fieldsRef.gender}
                size="xl"
                values={[
                  { label: t('form.user_details.gender_picker_female'), value: 'female' },
                  { label: t('form.user_details.gender_picker_male'), value: 'male' },
                  { label: t('form.user_details.gender_picker_other'), value: 'other' },
                ]}
              />

              <FormControl isInvalid={!!errors.interests} mt="4">
                <FormControl.Label>
                  <Text fontSize="md" mb="3" color="black">
                    {t('form.user_details.interests_label')} (Optional)
                  </Text>
                </FormControl.Label>
                {isLoadingInterests ? (
                  <Text>Loading interests...</Text>
                ) : (
                  <CheckBoxList
                    layout="grid"
                    checkBoxList={
                      interestsList?.map((interest) => ({
                        label: interest.label,
                        value: interest.value,
                        isChecked: localInterests.includes(interest.value),
                      })) || []
                    }
                    onPress={handleInterestPress}
                  />
                )}
                <FormControl.ErrorMessage>{errors.interests?.message}</FormControl.ErrorMessage>
              </FormControl>
            </Box>
            <Row width="full" justifyContent="space-between" my="6">
              <Button
                bg="offBlue"
                flex={0.48}
                isDisabled={isSubmitting}
                onPress={goBack}
                size="lg"
                testID="cancelButton"
              >
                <Text color="actionBlue" bold fontSize="md">
                  {t('common.cancel')}
                </Text>
              </Button>

              <Button
                bg="actionBlue"
                flex={0.48}
                isDisabled={isSubmitDisabled}
                isLoading={isSubmitting}
                onPress={handleSaveButtonPress}
                size="lg"
                testID="signInButton"
              >
                <Text bold color="white" fontSize="md">
                  {t('common.save')}
                </Text>
              </Button>
            </Row>
          </Box>
        </Box>
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  keyboardAwareScrollView: {
    alignItems: 'flex-start',
  },
  safeArea: {
    backgroundColor: RFColors['background'],
    flexGrow: 1,
    paddingHorizontal: 16,
    ...(Platform.OS === WEB && {
      minWidth: mobileMinWidth,
      maxWidth: userDetailsFormWidth,
      width: '100%',
      marginLeft: 'auto',
      marginRight: 'auto',
    }),
  },
})
